import { handleApiResponse, notifyInternalMessage } from '@/functions';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/components/api';
import routes from '@/lib/routes';
import type { ICoinPackages, IIncognitoPackages, IBoostPackages } from '@/interfaces/paymentPackage.interface';

type PackageType = 'coin' | 'incognito' | 'boost';

type PackageData<T extends PackageType> =
  T extends 'coin' ? ICoinPackages :
  T extends 'incognito' ? IIncognitoPackages :
  IBoostPackages;

const packageUrl: Record<PackageType, string> = {
  coin: routes.api.coinPackages,
  incognito: routes.api.incognitoPackages,
  boost: routes.api.boostPackages,
};

const emptyPackages = { AFRICA: [], EUROPE: [] };

export const useRequestGetPaymentPackages = <T extends PackageType>(type: T) => {
  const url = packageUrl[type];

  const { isLoading, isFetching, data, error } = useQuery({
    queryKey: [url],
    queryFn: ({ queryKey: [_url] }) =>
      apiClient
        .get<PackageData<T>>(_url as string)
        .then(handleApiResponse)
        .catch(notifyInternalMessage),
    refetchOnWindowFocus: false,
  });

  return {
    packages: (data as PackageData<T>) ?? emptyPackages,
    isLoading,
    isFetching,
    error,
  };
};
